import {
  errorCheckoutPaymentCreditCard,
  errorCheckoutPaymentDefault,
  errorCheckoutPaymentPIX,
} from "../constants"
import { IResponse } from "../types/ErrorResponse"
import { TPaymentMethodEnum, TProductFrequency } from "../types/Product"
import { sha256 } from "js-sha256"

const encryptSha256 = (value?: string) => {
  if (!value) {
    return ""
  }
  return sha256(value.toLocaleLowerCase())
}

const paymentMethodToString = (value?: string): string => {
  switch (value) {
    case "credit_card":
      return "Crédito"
    case "debit_card":
      return "Débito"
    case "boleto":
      return "Boleto"
    case "pix":
      return "PIX"
    default:
      return value ?? ""
  }
}

const checkoutOrderStatusToString = (value?: string): string => {
  switch (value) {
    case "pending":
      return "Pendente"
    case "paid":
      return "Pago"
    case "canceled":
      return "Cancelado"
    case "processing":
      return "Processando"
    case "failed":
      return "Falha"
    case "overpaid":
      return "Pago a mais"
    case "underpaid":
      return "Pago a menos"
    case "chargedback":
      return "Estorno"
    default:
      return value ?? ""
  }
}

const methodTypeToString = (value?: TPaymentMethodEnum) => {
  switch (value) {
    case TPaymentMethodEnum.CreditCard:
      return "Crédito"
    case TPaymentMethodEnum.DebitCard:
      return "Débito"
    case TPaymentMethodEnum.Pix:
      return "PIX"
    case TPaymentMethodEnum.Boleto:
      return "Boleto"
    default:
      return ""
  }
}

const methodTypeToPaymentMethod = (value?: TPaymentMethodEnum) => {
  switch (value) {
    case TPaymentMethodEnum.CreditCard:
      return "credit_card"
    case TPaymentMethodEnum.DebitCard:
      return "debit_card"
    case TPaymentMethodEnum.Pix:
      return "pix"
    case TPaymentMethodEnum.Boleto:
      return "boleto"
    default:
      return ""
  }
}

const productFrequencyToString = (value?: TProductFrequency) => {
  switch (value) {
    case TProductFrequency.Weekly:
      return "Semanal"
    case TProductFrequency.Monthly:
      return "Mensal"
    case TProductFrequency.Bimonthly:
      return "Bimestral"
    case TProductFrequency.Quarterly:
      return "Trimestral"
    case TProductFrequency.Semester:
      return "Semestral"
    case TProductFrequency.Yearly:
      return "Anual"
    default:
      return ""
  }
}

const productFrequencyToType = (value?: TProductFrequency) => {
  switch (value) {
    case TProductFrequency.Weekly:
      return "Semana"
    case TProductFrequency.Monthly:
      return "Mês"
    case TProductFrequency.Bimonthly:
      return "Bimestre"
    case TProductFrequency.Quarterly:
      return "Trimestre"
    case TProductFrequency.Semester:
      return "Semestre"
    case TProductFrequency.Yearly:
      return "Ano"
    default:
      return ""
  }
}

const getErrorPayment = (
  value: TPaymentMethodEnum | undefined,
  response?: IResponse
) => {
  if (response && response.error && response.error !== "") {
    if (response.error.includes("ReCAPTCHA")) {
      return "Não conseguimos verificar sua identidade. Por favor, tente novamente ou entre em contato com o suporte para assistência."
    }

    return response.error
  }

  switch (value) {
    case TPaymentMethodEnum.CreditCard:
      return errorCheckoutPaymentCreditCard
    case TPaymentMethodEnum.DebitCard:
      return errorCheckoutPaymentCreditCard
    case TPaymentMethodEnum.Pix:
      return errorCheckoutPaymentPIX
    case TPaymentMethodEnum.Boleto:
      return errorCheckoutPaymentPIX
    default:
      return errorCheckoutPaymentDefault
  }
}

const replaceSpacesAndNewlines = (input: string | null | undefined): string => {
  let result = ""
  if (input) {
    for (const char of input) {
      if (char === " " || char === "\n") {
        result += "-"
      } else {
        result += char
      }
    }
  }
  return result
}

/**
 * Conjunto de funções para conversão de valores
 * @property {const} encryptSha256 - Retorna o valor da criptografia sha 256, utilizado mais para enviar os dados para o Facebook.
 * @property {const} paymentMethodToString - Retorna a descrição do tipo de pagamento.
 * @property {const} checkoutOrderStatusToString - Retorna a descrição do status do pedido.
 * @property {const} methodTypeToString - Retorna o tipo de pagamento em string.
 * @property {const} methodTypeToPaymentMethod - Converte o tipo de pagamento para o método de pagamento.
 * @property {const} productFrequencyToString - Retorna a descrição do tipo de frequência.
 * @property {const} productFrequencyToType - Retorna a frequência do produto para a descrição do tipo de frequência.
 * @property {const} getErrorPayment - Retorna o erro padrão conforme o tipo de pagamento.
 * @property {const} replaceSpacesAndNewlines - Remove espaços e quebras de linha de uma string.
 */
const Convertions = {
  encryptSha256,
  paymentMethodToString,
  checkoutOrderStatusToString,
  methodTypeToString,
  methodTypeToPaymentMethod,
  productFrequencyToString,
  /**
   * Converte a frequência do produto para a descrição do tipo de frequência
   * @param value frequência do produto
   * @returns descrição do tipo de frequência
   */
  productFrequencyToType,
  getErrorPayment,
  /**
   * Remover espaços e quebras de linha de uma string
   * @param input string a ser tratada
   * @returns string tratada
   */
  replaceSpacesAndNewlines,
}

export default Convertions
